import type { ReactNode } from "react"
import { cn } from "@/lib/utils"

/**
 * Background art for the auth pages.
 *
 * Small, loose, hand-drawn marks that sit *behind* and *around* the content as
 * texture — a paper clip in a corner, a coffee ring, a little arrow, a star, a
 * squiggle, a stack of papers. Authored as inline SVG in the same one-stroke
 * hand as the rest of @/components/sketch, but far quieter: muted ink at a low
 * opacity, so the page reads first and the doodle second.
 *
 * Every one is decorative. They are `aria-hidden` and `pointer-events-none`, sit
 * absolutely in the margins, never behind text or a field, and are hidden below
 * `sm` where there is no margin to put them in.
 */

const INK = "var(--muted-foreground)"

function Doodle({
  children,
  viewBox,
  className,
}: {
  children: ReactNode
  viewBox: string
  className?: string
}) {
  return (
    <svg
      viewBox={viewBox}
      fill="none"
      aria-hidden="true"
      className={cn("pointer-events-none select-none", className)}
    >
      <g
        stroke={INK}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      >
        {children}
      </g>
    </svg>
  )
}

export function PaperClip({ className }: { className?: string }) {
  return (
    <Doodle viewBox="0 0 24 44" className={className}>
      <path d="M7 4 V30 a5 5 0 0 0 10 0 V9 a3 3 0 0 0 -6 0 V27" />
    </Doodle>
  )
}

export function CoffeeRing({ className }: { className?: string }) {
  return (
    <Doodle viewBox="0 0 48 48" className={className}>
      <path d="M8 27 A16 16 0 1 1 40 25" />
    </Doodle>
  )
}

export function ArrowDoodle({ className }: { className?: string }) {
  return (
    <Doodle viewBox="0 0 48 48" className={className}>
      <path d="M6 34 C14 46 32 44 38 30" />
      <path d="M38 30 L31 23" />
      <path d="M38 30 L44 26" />
    </Doodle>
  )
}

export function StarDoodle({ className }: { className?: string }) {
  return (
    <Doodle viewBox="0 0 48 48" className={className}>
      <path d="M24 6 C25.5 15 26 21 32 24 C26 27 25.5 33 24 42 C22.5 33 22 27 16 24 C22 21 22.5 15 24 6 Z" />
    </Doodle>
  )
}

export function SquiggleDoodle({ className }: { className?: string }) {
  return (
    <Doodle viewBox="0 0 48 20" className={className}>
      <path d="M4 10 C8 3 12 17 16 10 C20 3 24 17 28 10 C32 3 36 17 40 10 C42 6.5 44 6 44 7" />
    </Doodle>
  )
}

export function PaperStack({ className }: { className?: string }) {
  return (
    <Doodle viewBox="0 0 48 48" className={className}>
      <path d="M14 14 h20 a2 2 0 0 1 2 2 v20 a2 2 0 0 1 -2 2 h-20 a2 2 0 0 1 -2 -2 v-20 a2 2 0 0 1 2 -2 Z" />
      <path d="M18 10 h20 a2 2 0 0 1 2 2 v20 a2 2 0 0 1 -2 2 h-20 a2 2 0 0 1 -2 -2 v-20 a2 2 0 0 1 2 -2 Z" />
      <path d="M22 6 h20 a2 2 0 0 1 2 2 v20 a2 2 0 0 1 -2 2 h-20 a2 2 0 0 1 -2 -2 v-20 a2 2 0 0 1 2 -2 Z" />
    </Doodle>
  )
}
